import { createAction, props } from '@ngrx/store';

import { EntityListEntity } from '../components/entity-list/entity-list.entity';

interface IPersistenceInfo {
	isPersisted: boolean;
	partitionCount: number;
	persistenceDate: Date | null;
	persistenceDuration: number;
	persistenceSize: number;
}

export enum EntityActions {
	InitEntityList = '[Entity] get entity list',
	RefreshEntityList = '[BE][Entity] refresh entity list',
	FetchEntityListFail = '[BE][Entity] fetch entity list fail',
	FetchEntityListSuccess = '[BE][Entity] fetch entity list success',
	UpdateEntitiesFrameStatus = '[BE][Entity] get entities frame status',
	UpdateEntitiesFrameStatusSuccess = '[BE][Entity] get entities frame status success',
	UpdateEntitiesFrameStatusFailure = '[BE][Entity] get entities frame status failure',
	ConstructEntityList = '[Entity] construct entity list',
	ConstructEntityListSuccess = '[BE][Entity] construct entity list success',
	ConstructEntityListFail = '[BE][Entity] construct entity list failure',
	NavigateToEntityRoute = '[Entity] navigate to entity route',
	NavigateToEntityCreateRoute = '[Entity] navigate to entity create route',
	NavigateToEntityDuplicateRoute = '[Entity] navigate to entity duplicate route',
	NavigateToCreateFromEntityRoute = '[Entity] navigate to create from entity route',
	UpdateIncludePublic = '[Entity] update include public',
	OpenPersistConfirmationPopup = '[Entity] open persist confirm popup',
	PersistEntity = '[Entity] persist entity',
	PersistEntityStarted = '[Entity] persist entity started',
	PersistEntityStopped = '[Entity] persist entity stopped',
	PersistanceActionFinish = '[Entity] persistence action finished',
	PersistEntityFail = '[BE][Entity] persist entity fail',
	TrackEntityAction = '[BE][Entity] track entity action',
	OpenDeleteConfirmationPopup = '[Entity] open delete confirm popup',
	DeleteEntity = '[Entity] delete entity',
	DeleteEntityFail = '[BE][Entity] delete entity fail',
	BulkDelete = '[Entity] delete many entities',
	OpenRepersistConfirmationPopup = '[Entity] open repersist confirm popup',
	RepersistEntity = '[Entity] repersist entity',
	RepersistEntityStarted = '[Entity] repersist entity started',
	RepersistEntityStopped = '[Entity] repersist entity stopped',
	RepersistEntityFail = '[BE][Entity] repersist entity fail',
	OpenUnpersistConfirmationPopup = '[Entity] open unpersist confirm popup',
	UnpersistEntity = '[Entity] unpersist entity',
	UnpersistEntityStarted = '[Entity] unpersist entity started',
	UnpersistEntityStopped = '[Entity] unpersist entity stopped',
	UnpersistEntityFail = '[BE][Entity] unpersist entity fail',
	OpenCreateDatablocksConfirmationPopup = '[Entity] open create datablocks confirm popup',
	CreateDatablocksFromEntities = '[Entity] create datablocks from entities',
	ToggleEntityStatus = '[Entity] toggle entity status',
	FetchPublicProjectDetails = '[BE][Entity] get public project details',
	FetchPublicProjectDetailsSuccess = '[BE][Entity] get public project details success',
	FetchPublicProjectDetailsFailure = '[BE][Entity] get public project details failure',
	StopPersistence = '[Entity] stop persistence',
}

export const initEntityList = createAction(EntityActions.InitEntityList);
export const refreshEntityList = createAction(EntityActions.RefreshEntityList);

export const fetchEntityListSuccess = createAction(
	EntityActions.FetchEntityListSuccess,
	props<{ entityList: EntityListEntity }>()
);

export const fetchEntityListFail = createAction(
	EntityActions.FetchEntityListFail,
	props<{ error: unknown }>()
);

export const updateEntitiesFrameStatus = createAction(
	EntityActions.UpdateEntitiesFrameStatus,
	props<{ ids: Array<number>; shouldReload: boolean }>()
);

export const updateEntitiesFrameStatusSuccess = createAction(
	EntityActions.UpdateEntitiesFrameStatusSuccess,
	props<{ entityList: EntityListEntity }>()
);

export const updateEntitiesFrameStatusFailure = createAction(
	EntityActions.UpdateEntitiesFrameStatusFailure,
	props<{ error: unknown }>()
);

export const constructEntityList = createAction(
	EntityActions.ConstructEntityList
);

export const constructEntityListSuccess = createAction(
	EntityActions.ConstructEntityListSuccess,
	props<{ entityList: EntityListEntity }>()
);

export const constructEntityListFail = createAction(
	EntityActions.ConstructEntityListFail,
	props<{ error: unknown }>()
);

export const navigateToEntity = createAction(
	EntityActions.NavigateToEntityRoute,
	props<{ id: number }>()
);
export const navigateToEntityCreate = createAction(
	EntityActions.NavigateToEntityCreateRoute
);
export const navigateToEntityDuplicate = createAction(
	EntityActions.NavigateToEntityDuplicateRoute,
	props<{ id: number }>()
);
export const navigateToCreateFromEntity = createAction(
	EntityActions.NavigateToCreateFromEntityRoute,
	props<{ id: number }>()
);

export const updateIncludePublic = createAction(
	EntityActions.UpdateIncludePublic,
	props<{ includePublic: boolean }>()
);

export const openPersistConfirmationPopup = createAction(
	EntityActions.OpenPersistConfirmationPopup,
	props<{
		entityId: Array<number>;
		entityLabel: string;
	}>()
);

export const persistEntity = createAction(
	EntityActions.PersistEntity,
	props<{
		entityId: number;
		entityCode: string;
		entityLabel: string;
	}>()
);

export const persistEntityStarted = createAction(
	EntityActions.PersistEntityStarted,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const persistEntityStopped = createAction(
	EntityActions.PersistEntityStopped,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const persistEntityFail = createAction(
	EntityActions.PersistEntityFail,
	props<{ error: unknown }>()
);

export const persistenceActionFinish = createAction(
	EntityActions.PersistanceActionFinish,
	props<{
		entityId: number;
		persistenceInfo: IPersistenceInfo;
	}>()
);

export const trackAction = createAction(
	EntityActions.TrackEntityAction,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const openDeleteConfirmationPopup = createAction(
	EntityActions.OpenDeleteConfirmationPopup,
	props<{ entityId: Array<number>; entityLabel: string }>()
);

export const deleteEntity = createAction(
	EntityActions.DeleteEntity,
	props<{ entityId: number }>()
);

export const bulkDelete = createAction(
	EntityActions.BulkDelete,
	props<{ entityId: Array<number> }>()
);

export const deleteEntityFail = createAction(EntityActions.DeleteEntityFail);

export const openRepersistConfirmationPopup = createAction(
	EntityActions.OpenRepersistConfirmationPopup,
	props<{
		entityId: number;
		entityLabel: string;
	}>()
);

export const repersistEntity = createAction(
	EntityActions.RepersistEntity,
	props<{
		entityId: number;
		entityCode: string;
		entityLabel: string;
	}>()
);

export const repersistEntityStarted = createAction(
	EntityActions.RepersistEntityStarted,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const repersistEntityStopped = createAction(
	EntityActions.RepersistEntityStopped,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const repersistEntityFail = createAction(
	EntityActions.RepersistEntityFail,
	props<{ error: unknown }>()
);

export const openUnpersistConfirmationPopup = createAction(
	EntityActions.OpenUnpersistConfirmationPopup,
	props<{
		entityId: Array<number>;
		entityLabel: string;
	}>()
);

export const unpersistEntity = createAction(
	EntityActions.UnpersistEntity,
	props<{
		entityId: number;
		entityCode: string;
		entityLabel: string;
	}>()
);

export const unpersistEntityStarted = createAction(
	EntityActions.UnpersistEntityStarted,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const unpersistEntityStopped = createAction(
	EntityActions.UnpersistEntityStopped,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const unpersistEntityFail = createAction(
	EntityActions.UnpersistEntityFail,
	props<{ error: unknown }>()
);

export const openCreateDatablocksConfirmationPopup = createAction(
	EntityActions.OpenCreateDatablocksConfirmationPopup,
	props<{
		entityId: Array<number>;
	}>()
);

export const createDatablocksFromEntities = createAction(
	EntityActions.CreateDatablocksFromEntities,
	props<{ entityId: Array<number> }>()
);

export const toggleEntityStatus = createAction(
	EntityActions.ToggleEntityStatus,
	props<{ entityId: number }>()
);

export const fetchPublicProjectDetails = createAction(
	EntityActions.FetchPublicProjectDetails,
	props<{ entityId: number }>()
);

export const fetchPublicProjectDetailsSuccess = createAction(
	EntityActions.FetchPublicProjectDetailsSuccess,
	props<{ characCount: number; depotCount: number; entityId: number }>()
);

export const fetchPublicProjectDetailsFail = createAction(
	EntityActions.FetchPublicProjectDetailsFailure,
	props<{ error: unknown }>()
);

export const stopPersistence = createAction(
	EntityActions.StopPersistence,
	props<{ jobKey: string }>()
);
